// ================================
// useSpeechRecognition
// ================================

/**
 * Custom hook that manages client-side Speech-to-Text using Azure Cognitive Services.
 *
 * ## How it works
 * 1. On the first call to `startListening`, the hook fetches a short-lived API key
 *    and endpoint URL from the backend (`/speech/api/speech-config-stt`).
 * 2. It builds an Azure `SpeechRecognizer` pointed at the Universal v2 STT endpoint
 *    over a secure WebSocket (`wss://`).
 * 3. Continuous recognition is started, which streams audio from the default
 *    microphone indefinitely until `stopListening` is called or an error occurs.
 * 4. Two types of results are emitted:
 *    - **Interim** (`recognizing` event) — partial, in-progress text updated in real time.
 *    - **Final** (`recognized` event) — a complete, committed utterance.
 *
 * ## Lifecycle notes
 * - The SDK is initialized at most once per hook instance (`isInitializedRef` guard).
 * - The recognizer and audio config are stored in refs so they survive re-renders
 *   without triggering additional effect runs.
 * - On unmount the recognizer is stopped and closed to release the microphone and
 *   terminate the WebSocket connection.
 *
 * @param options.onTranscript - Callback fired for both interim and final results.
 *   Receives `(text, isFinal)` — `isFinal` is `false` for interim, `true` for final.
 * @param options.onError      - Callback fired whenever a recognition error occurs.
 * @param options.autoStart    - When `true`, recognition starts automatically on mount.
 *   Defaults to `false`.
 *
 * @returns An object with the current `status`, `isListening` convenience flag,
 *   the latest `transcript`, `startListening` / `stopListening` handlers, and any
 *   `error` message.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

// ================================
// Types & Constants
// ================================

/**
 * Shape of the speech configuration object returned by the backend.
 * The `key` is a short-lived Azure Cognitive Services subscription key and
 * `endpoint` is the full HTTPS URL of the regional STT endpoint.
 */
export interface SpeechConfig {
  key: string;
  endpoint: string;
}

/**
 * Enumeration of all possible recognition lifecycle states.
 *
 * - `IDLE`         — No active session; the hook is ready to start.
 * - `INITIALIZING` — Fetching config and setting up the Azure SDK (async, one-time).
 * - `LISTENING`    — Continuous recognition is actively streaming audio.
 * - `ERROR`        — A fatal error occurred; `error` state will contain details.
 */
export const RecognitionStatus = {
  /** No active session; the hook is ready to start. */
  IDLE: 'idle',
  /** Fetching config and setting up the Azure SDK (async, one-time). */
  INITIALIZING: 'initializing',
  /** Continuous recognition is actively streaming audio. */
  LISTENING: 'listening',
  /** A fatal error occurred; `error` state will contain details. */
  ERROR: 'error',
} as const;

/** Union type derived from `RecognitionStatus` values for strong typing. */
export type RecognitionStatusValue = (typeof RecognitionStatus)[keyof typeof RecognitionStatus];

// ================================
// Hook Interfaces
// ================================

/**
 * Configuration options accepted by `useSpeechRecognition`.
 */
interface UseSpeechRecognitionOptions {
  /**
   * Fired on every transcript update (both interim and final).
   * @param text    - The recognised text fragment.
   * @param isFinal - `true` when the utterance is committed; `false` for interim results.
   */
  onTranscript?: (text: string, isFinal: boolean) => void;
  /**
   * Fired whenever a recognition or initialisation error occurs.
   * @param error - Human-readable error description.
   */
  onError?: (error: string) => void;
  /**
   * When `true`, `startListening` is called automatically on mount.
   * Defaults to `false`.
   */
  autoStart?: boolean;
}

/**
 * Values returned by `useSpeechRecognition`.
 */
interface UseSpeechRecognitionReturn {
  /** Current lifecycle status of the recognizer. */
  status: RecognitionStatusValue;
  /** Convenience boolean — `true` when `status === 'listening'`. */
  isListening: boolean;
  /** The most recent transcript text (interim or final). */
  transcript: string;
  /** Initialises the SDK if needed, then begins continuous recognition. */
  startListening: () => Promise<void>;
  /** Stops the active continuous recognition session. */
  stopListening: () => void;
  /** Non-null when the hook is in the ERROR state; contains a description. */
  error: string | null;
}

// ================================
// Hook Implementation
// ================================

export const useSpeechRecognition = ({
  onTranscript,
  onError,
  autoStart = false,
}: UseSpeechRecognitionOptions): UseSpeechRecognitionReturn => {
  // ======================================
  // State
  // ======================================

  /** Current lifecycle status — drives the UI and `isListening` flag. */
  const [status, setStatus] = useState<RecognitionStatusValue>(RecognitionStatus.IDLE);

  /** Latest transcript text. Cleared at the start of each new `startListening` call. */
  const [transcript, setTranscript] = useState('');

  /** Holds an error message when in the ERROR state; null otherwise. */
  const [error, setError] = useState<string | null>(null);

  // ======================================
  // Refs
  // ======================================

  /**
   * Holds the active `SpeechRecognizer` instance.
   * Stored in a ref so it persists across re-renders without causing them.
   */
  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);

  /**
   * Holds the `AudioConfig` instance tied to the default microphone.
   * Retained here so it can be referenced for potential future cleanup needs
   * without being recreated on every render.
   */
  const audioConfigRef = useRef<SpeechSDK.AudioConfig | null>(null);

  /**
   * Guards against re-initializing the SDK on subsequent `startListening` calls.
   * Set to `true` after the recognizer is successfully created.
   */
  const isInitializedRef = useRef(false);

  // ================================
  // Private Helpers
  // ================================

  /**
   * Fetches the Azure Speech Service configuration (key + endpoint) from the backend.
   *
   * The backend acts as a proxy so the Azure subscription key is never exposed to
   * the client directly. The response is a `SpeechConfig` JSON object.
   *
   * @throws {Error} When the HTTP response is not OK (e.g. 401, 500).
   * @returns The `SpeechConfig` containing the API key and regional endpoint.
   */
  const fetchSpeechConfig = async (): Promise<SpeechConfig> => {
    const backendUrl = process.env.NEXT_PUBLIC_VITE_BACKEND_URL;
    const response = await fetch(`${backendUrl}/speech/api/speech-config-stt`);
    console.log('Fetching speech config from:', `${backendUrl}/speech/api/speech-config-stt`);

    if (!response.ok) {
      throw new Error(`Failed to fetch speech config: ${response.statusText}`);
    }

    return response.json();
  };

  /**
   * Initializes the Azure Speech SDK and wires up all SDK event handlers.
   *
   * This function is intentionally idempotent — it returns early when the
   * recognizer has already been created (`isInitializedRef.current === true`),
   * so it is safe to call from `startListening` on every invocation.
   *
   * ## Steps
   * 1. Set status to INITIALIZING and clear any previous error.
   * 2. Fetch the Azure key + endpoint from the backend.
   * 3. Build a `SpeechConfig` using the Universal v2 WebSocket endpoint.
   * 4. Override the three Azure properties that control silence/segmentation
   *    timeouts (see inline comments for why each is needed).
   * 5. Create an `AudioConfig` from the default microphone.
   * 6. Instantiate the `SpeechRecognizer` and attach event handlers.
   * 7. Mark `isInitializedRef` as `true`.
   *
   * On any failure the error is surfaced via state and `onError`, and status
   * is set to ERROR so the caller can react appropriately.
   */
  const initializeRecognizer = async () => {
    // Short-circuit: SDK already set up for this hook instance.
    if (isInitializedRef.current) {
      console.log('Speech recognizer already initialized');
      return;
    }

    try {
      setStatus(RecognitionStatus.INITIALIZING);
      setError(null);

      // Fetch speech config from backend
      const config = await fetchSpeechConfig();

      // Extract hostname from endpoint for fromHost.
      // The Azure SDK's `fromEndpoint` requires a WebSocket URL, so we parse
      // the HTTPS endpoint returned by the backend to get just the hostname,
      // then reconstruct the WSS URL for the Universal v2 STT path.
      const endpointUrl = new URL(config.endpoint);
      const hostname = endpointUrl.hostname;

      // Create speech config using the regional WSS endpoint and the API key.
      const speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(
        new URL(`wss://${hostname}/stt/speech/universal/v2`),
        config.key,
      );
      speechConfig.speechRecognitionLanguage = 'en-US';

      // Extend segmentation timeout to prevent premature cutoffs during long utterances.
      // EndSilenceTimeoutMs controls how long continuous silence is tolerated before
      // Azure automatically ends the recognition segment. Setting it to 5 minutes
      // ensures a brief pause mid-sentence does not prematurely cut off the session.
      // @ts-ignore - Azure Speech SDK type issue
      speechConfig.setProperty(
        'SpeechServiceConnection_EndSilenceTimeoutMs',
        '300000', // 5 minutes of silence before ending
      );

      // InitialSilenceTimeoutMs: Prevent timeout before speech starts.
      // Without this, Azure would time out if the user doesn't begin speaking
      // within the default window (typically ~5 seconds), which is too short
      // for use cases where the mic is opened before the user is ready to speak.
      // @ts-ignore - Azure Speech SDK type issue
      speechConfig.setProperty(
        'SpeechServiceConnection_InitialSilenceTimeoutMs',
        '300000', // 5 minutes before initial timeout
      );

      // Disable automatic sentence-level segmentation during continuous speech.
      // By default, Azure inserts forced segment boundaries every ~30–50 seconds
      // of continuous audio. Setting this to 'None' prevents those artificial
      // breaks so the full utterance is delivered as a single recognised block.
      // @ts-ignore - Azure Speech SDK type issue
      speechConfig.setProperty('SpeechServiceResponse_RequestSentenceBoundary', 'None');

      // Configure audio with echo cancellation.
      // `fromDefaultMicrophoneInput` selects the system's default input device
      // and applies platform-level acoustic echo cancellation (AEC) where available.
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      audioConfigRef.current = audioConfig;

      // Create recognizer by wiring the speech config to the audio config.
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      recognizerRef.current = recognizer;

      // ======================================
      // SDK Event Handlers
      // ======================================

      // Event: Recognizing — fired continuously as the user speaks.
      // These are *interim* results: real-time partial text that will be refined
      // until the utterance is committed. `isFinal` is passed as `false` so
      // consumers know not to treat this as a completed sentence.
      recognizer.recognizing = (_s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
          const interimText = e.result.text;
          setTranscript(interimText);
          onTranscript?.(interimText, false);
        }
      };

      // Event: Recognized — fired once per committed utterance.
      // These are *final* results: the best transcription Azure could produce for
      // a completed speech segment. Whitespace-only results are filtered out to
      // avoid polluting the transcript with empty segments (e.g. ambient noise).
      recognizer.recognized = (_s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const finalText = e.result.text;
          if (finalText.trim()) {
            setTranscript(finalText);
            onTranscript?.(finalText, true);
          }
        }
      };

      // Event: Canceled — fired when recognition is terminated due to an error
      // (e.g. network loss, auth failure, or microphone access denied).
      // We surface the error, set status to ERROR, notify the caller, and then
      // explicitly stop the async recognition loop to release resources.
      recognizer.canceled = (_s, e) => {
        console.error('Speech recognition canceled:', e.errorDetails);
        const errorMsg = `Recognition canceled: ${e.errorDetails}`;
        setError(errorMsg);
        setStatus(RecognitionStatus.ERROR);
        onError?.(errorMsg);

        recognizer.stopContinuousRecognitionAsync();
      };

      // Event: Session stopped — fired when the recognition session ends cleanly
      // (e.g. after `stopContinuousRecognitionAsync` completes or the service
      // closes the connection gracefully). Reset to IDLE and ensure the async
      // loop is fully terminated.
      recognizer.sessionStopped = () => {
        setStatus(RecognitionStatus.IDLE);
        recognizer.stopContinuousRecognitionAsync();
      };

      // Mark the recognizer as ready so subsequent calls to `startListening`
      // skip this entire initialization block.
      isInitializedRef.current = true;
    } catch (err) {
      // Normalize the caught value to a string — the SDK can throw both Error
      // objects and plain strings in different failure scenarios.
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('Failed to initialize speech recognizer:', errorMsg);
      setError(errorMsg);
      setStatus(RecognitionStatus.ERROR);
      onError?.(errorMsg);
    }
  };

  // ================================
  // Public Handlers
  // ================================

  /**
   * Starts continuous speech recognition.
   *
   * On the first call, the Azure SDK is initialized (config fetch + recognizer
   * setup). Subsequent calls reuse the existing recognizer instance.
   *
   * The transcript is cleared before each new session so stale text from a
   * previous session does not bleed through.
   *
   * Errors during initialization or SDK startup are caught, stored in state,
   * and forwarded to `onError`.
   *
   * Note: The empty dependency array on `useCallback` is intentional. The
   * `initializeRecognizer` function closes over refs (not state), so it does
   * not need to be in the deps array to remain correct across renders.
   */
  const startListening = useCallback(async () => {
    try {
      // Initialize on first call only — guarded internally by `isInitializedRef`.
      if (!isInitializedRef.current) {
        await initializeRecognizer();
      }

      // Guard: if initialization failed, `recognizerRef` will still be null.
      // Throw so the outer catch can surface the error consistently.
      if (!recognizerRef.current) {
        throw new Error('Speech recognizer not initialized');
      }

      // Optimistically set LISTENING before the async SDK call resolves so the
      // UI can show an active state immediately.
      setStatus(RecognitionStatus.LISTENING);

      // Clear any previous transcript so the new session starts fresh.
      setTranscript('');

      // Begin streaming audio to Azure. The success callback confirms the session
      // is truly active; the error callback handles SDK-level start failures.
      recognizerRef.current.startContinuousRecognitionAsync(
        () => {
          // Confirm status is LISTENING once the SDK acknowledges the session start.
          setStatus(RecognitionStatus.LISTENING);
        },
        (err) => {
          console.error('Failed to start recognition:', err);
          const errorMsg = `Failed to start: ${err}`;
          setError(errorMsg);
          setStatus(RecognitionStatus.ERROR);
          onError?.(errorMsg);
        },
      );
    } catch (err) {
      // Catches both initialization errors and the 'not initialized' guard above.
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('Error starting recognition:', errorMsg);
      setError(errorMsg);
      setStatus(RecognitionStatus.ERROR);
      onError?.(errorMsg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Stops the active continuous recognition session.
   *
   * No-ops silently when the recognizer has not been initialized yet (e.g. if
   * `stopListening` is called before `startListening`). This is intentional to
   * avoid throwing in teardown scenarios where the component may unmount before
   * the session ever started.
   */
  const stopListening = useCallback(() => {
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          // Recognition stopped cleanly — return to the idle state.
          setStatus(RecognitionStatus.IDLE);
        },
        (err) => {
          // SDK reported a failure while stopping (e.g. already closed connection).
          console.error('Failed to stop recognition:', err);
          setStatus(RecognitionStatus.ERROR);
        },
      );
    }
  }, []);

  // ================================
  // Effects
  // ================================

  /**
   * Auto-start effect and unmount cleanup.
   *
   * - When `autoStart` is `true`, kicks off `startListening` immediately after
   *   the component mounts so consumers don't need to call it manually.
   * - The cleanup function runs on unmount and ensures the microphone is released
   *   and the Azure WebSocket is closed, regardless of whether recognition was
   *   ever started. This prevents microphone indicator persisting after navigation.
   */
  // Auto-start if enabledcode .
  useEffect(() => {
    if (autoStart) {
      startListening();
    }

    // Cleanup on unmount
    return () => {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync();
        recognizerRef.current.close();
      }
    };
  }, [autoStart, startListening]);

  // ================================
  // Public API
  // ================================

  return {
    status,
    /** Derived convenience flag — avoids `status === RecognitionStatus.LISTENING` at call sites. */
    isListening: status === RecognitionStatus.LISTENING,
    transcript,
    startListening,
    stopListening,
    error,
  };
};
